import { Editor as SlateEditor, Transforms, Element, Range, NodeEntry, TextUnit } from 'slate';
import { RenderElementProps } from 'slate-react';
import { CustomEditor, LayoutElement } from 'slate/types/slate';
import { defaultParagraph } from 'slate/extensions/paragraph';
import { isPage } from 'slate/extensions/page';
import { hasText } from 'slate/functions';
import { Layout } from './components/Layout';
import { isLayout, emptyLayout } from './utils/layoutUtils';

const layout =
  (options: { markEmpty?: boolean } = {}) =>
  (editor: CustomEditor) => {
    const markEmpty = options.markEmpty ?? false;
    const { normalizeNode, renderElement, deleteBackward, deleteForward } = editor;

    const normalizePage = (entry: NodeEntry) => {
      const [node, path] = entry;
      if (!isPage(node)) {
        normalizeNode(entry);
        return;
      }
      const firstChild = node.children[0];
      if (Element.isElement(firstChild) && firstChild.type === 'layout') {
        normalizeNode(entry);
        return;
      }

      if (node.children.length) {
        Transforms.wrapNodes(editor, { type: 'layout', children: [] }, { at: [...path, 0] });
      } else {
        Transforms.insertNodes(editor, emptyLayout(), { at: [...path, 0] });
      }
    };

    const normalizeLayout = (entry: NodeEntry) => {
      const [node, path] = entry;
      if (!isLayout(node)) {
        normalizeNode(entry);
        return;
      }
      // We're good if the layout already has children
      const hasChildElements = !!node.children?.find((child) => Element.isElement(child));
      const isLayoutLeaf = !node.children?.find((child) => isLayout(child));
      const isEmptyLayoutLeaf = isLayoutLeaf && !hasText(node);

      if (isEmptyLayoutLeaf !== node.isEmptyLayoutLeaf) {
        Transforms.setNodes(editor, { isEmptyLayoutLeaf } as Partial<LayoutElement>, { at: path });
        return;
      }

      if (isLayoutLeaf !== node.isLayoutLeaf) {
        Transforms.setNodes(editor, { isLayoutLeaf } as Partial<LayoutElement>, { at: path });
        return;
      }

      if (hasChildElements) {
        if (isLayoutLeaf) {
          const lastChild = node.children[node.children.length - 1];
          if (Element.isElement(lastChild) && editor.isVoid(lastChild)) {
            Transforms.insertNodes(editor, defaultParagraph(), {
              at: [...path, node.children.length],
            });
            return;
          }
        }
        normalizeNode(entry);
        return;
      }

      Transforms.insertNodes(editor, defaultParagraph(), {
        at: [...path, 0],
      });
    };

    editor.normalizeNode = (entry) => {
      const [node] = entry;
      if (isLayout(node)) {
        normalizeLayout(entry);
      } else if (isPage(node)) {
        normalizePage(entry);
      } else {
        normalizeNode(entry);
      }
    };

    editor.renderElement = (props: RenderElementProps) => {
      if (isLayout(props.element)) {
        return <Layout {...props} markEmpty={markEmpty} />;
      } else {
        return renderElement(props);
      }
    };

    const isInEmptyLayoutLeaf = () => {
      const [currentLayoutLeaf] = Array.from(
        SlateEditor.nodes(editor, {
          match: (n) => isLayout(n) && !!n.isLayoutLeaf,
          mode: 'lowest',
        })
      );
      const leafNode = currentLayoutLeaf?.[0];
      return leafNode && isLayout(leafNode) ? leafNode.isEmptyLayoutLeaf : undefined;
    };

    editor.deleteBackward = (unit: TextUnit) => {
      if (!editor.selection) {
        deleteBackward(unit);
        return;
      }

      // For expanded selections, delegate to deleteFragment which handles cross-layout safely
      if (Range.isExpanded(editor.selection)) {
        editor.deleteFragment({ direction: 'backward' });
        return;
      }

      // Never delete from an empty layout box
      if (isInEmptyLayoutLeaf()) {
        return;
      }

      // Default behaviour if in middle of text
      if (editor.selection.focus.offset !== 0) {
        deleteBackward(unit);
        return;
      }

      const before = SlateEditor.before(editor, editor.selection.anchor.path);
      if (!before) {
        deleteBackward(unit);
        return;
      }
      const layoutBefore = Array.from(
        SlateEditor.nodes(editor, {
          at: before,
          match: isLayout,
          mode: 'lowest',
        })
      )[0][0];

      const layoutCurrent = Array.from(
        SlateEditor.nodes(editor, {
          match: isLayout,
          mode: 'lowest',
        })
      )[0][0];

      // Prevent text to be moved between layout boxes with backspace
      if (layoutBefore !== layoutCurrent) {
        return;
      }

      deleteBackward(unit);
    };

    editor.deleteForward = (unit: TextUnit) => {
      if (!editor.selection) {
        deleteForward(unit);
        return;
      }

      // For expanded selections, delegate to deleteFragment which handles cross-layout safely
      if (Range.isExpanded(editor.selection)) {
        editor.deleteFragment({ direction: 'forward' });
        return;
      }

      // Never delete from an empty layout box
      if (isInEmptyLayoutLeaf()) {
        return;
      }

      // Default behaviour if not at end of text
      const { anchor } = editor.selection;
      const [leaf] = SlateEditor.leaf(editor, anchor);
      if (anchor.offset < leaf.text.length) {
        deleteForward(unit);
        return;
      }

      const after = SlateEditor.after(editor, editor.selection.anchor.path);
      if (!after) {
        return;
      }

      const layoutAfter = Array.from(
        SlateEditor.nodes(editor, {
          at: after,
          match: isLayout,
          mode: 'lowest',
        })
      )[0]?.[0];

      const layoutCurrent = Array.from(
        SlateEditor.nodes(editor, {
          match: isLayout,
          mode: 'lowest',
        })
      )[0]?.[0];

      // Prevent text to be moved between layout boxes with forward delete
      if (layoutAfter !== layoutCurrent) {
        return;
      }

      deleteForward(unit);
    };

    return editor;
  };

export default layout;
